import React, { useState, useEffect, useMemo, memo } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Paper,
  InputAdornment,
  CircularProgress,
  Chip
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { doctorApiService } from '../../../../services/doctorApiService';
import type { Doctor } from '../../../../types/doctor';

interface DoctorMultiSelectProps {
  selectedDoctorIds: string[];
  onChange: (doctorIds: string[]) => void;
  disabled?: boolean;
}

/**
 * 医師複数選択コンポーネント
 *
 * - チェックボックス付き医師リスト
 * - 「全員選択」オプション
 * - 医師名検索機能
 * - 選択数表示
 */
const DoctorMultiSelect: React.FC<DoctorMultiSelectProps> = memo(({
  selectedDoctorIds,
  onChange,
  disabled = false
}) => {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // 医師一覧取得
  useEffect(() => {
    const fetchDoctors = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let allDoctors: Doctor[] = [];
        let currentPage = 1;
        let hasMorePages = true;
        const maxPerPage = 100;

        while (hasMorePages) {
          const response = await doctorApiService.getDoctors({
            page: currentPage,
            per_page: maxPerPage
          });

          allDoctors = [...allDoctors, ...response.data];

          if (response.meta && response.meta.current_page < response.meta.last_page) {
            currentPage++;
          } else {
            hasMorePages = false;
          }
        }

        setDoctors(allDoctors);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : '医師一覧の取得に失敗しました';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDoctors();
  }, []);

  // フィルタリングされた医師リスト
  const filteredDoctors = useMemo(() => {
    if (!searchTerm.trim()) {
      return doctors;
    }

    const term = searchTerm.toLowerCase();
    return doctors.filter(doctor =>
      doctor.first_name.toLowerCase().includes(term) ||
      doctor.last_name.toLowerCase().includes(term) ||
      doctor.doctor_code.toLowerCase().includes(term)
    );
  }, [doctors, searchTerm]);

  // 全選択状態
  const isAllSelected = useMemo(() => {
    return filteredDoctors.length > 0 &&
      filteredDoctors.every(d => selectedDoctorIds.includes(d.doctor_code));
  }, [filteredDoctors, selectedDoctorIds]);

  // 一部選択状態
  const isIndeterminate = useMemo(() => {
    const selectedCount = filteredDoctors.filter(d =>
      selectedDoctorIds.includes(d.doctor_code)
    ).length;
    return selectedCount > 0 && selectedCount < filteredDoctors.length;
  }, [filteredDoctors, selectedDoctorIds]);

  const handleToggle = (doctorCode: string) => {
    if (disabled) return;

    const newSelected = selectedDoctorIds.includes(doctorCode)
      ? selectedDoctorIds.filter(id => id !== doctorCode)
      : [...selectedDoctorIds, doctorCode];

    onChange(newSelected);
  };

  const handleToggleAll = () => {
    if (disabled) return;

    if (isAllSelected) {
      // 全解除
      const filteredIds = new Set(filteredDoctors.map(d => d.doctor_code));
      const newSelected = selectedDoctorIds.filter(id => !filteredIds.has(id));
      onChange(newSelected);
    } else {
      // 全選択
      const allIds = new Set([
        ...selectedDoctorIds,
        ...filteredDoctors.map(d => d.doctor_code)
      ]);
      onChange(Array.from(allIds));
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 2 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Box>
      {/* 選択数表示 */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          選択中:
        </Typography>
        <Chip
          label={`${selectedDoctorIds.length}名`}
          size="small"
          color={selectedDoctorIds.length > 0 ? 'primary' : 'default'}
        />
      </Box>

      {/* 検索フィールド */}
      <TextField
        fullWidth
        size="small"
        placeholder="医師名または医師コードで検索..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          )
        }}
        sx={{ mb: 2 }}
      />

      {/* 医師リスト */}
      <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
        <List dense disablePadding>
          {/* 全員選択 */}
          <ListItem disablePadding sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <ListItemButton onClick={handleToggleAll} disabled={disabled}>
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  disabled={disabled}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={500}>
                    全員選択 ({filteredDoctors.length}名)
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>

          {/* 医師一覧 */}
          {filteredDoctors.map((doctor) => (
            <ListItem
              key={doctor.doctor_code}
              disablePadding
            >
              <ListItemButton
                onClick={() => handleToggle(doctor.doctor_code)}
                disabled={disabled}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedDoctorIds.includes(doctor.doctor_code)}
                    disabled={disabled}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`${doctor.first_name}${doctor.last_name}先生`}
                  secondary={doctor.doctor_code}
                />
              </ListItemButton>
            </ListItem>
          ))}

          {filteredDoctors.length === 0 && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2" color="text.secondary" align="center">
                    該当する医師が見つかりません
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );
});

DoctorMultiSelect.displayName = 'DoctorMultiSelect';

export default DoctorMultiSelect;
